import Cropper from 'cropperjs'

const CARD_IMAGE_ASPECT_RATIO = 16 / 15
const OUTPUT_WIDTH = 1600
const OUTPUT_HEIGHT = 1500
const READY_EVENTS = ['DOMContentLoaded', 'turbo:load']

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => {
    canvas.toBlob(resolve, type, quality)
  })
}

function buildCroppedFilename(filename) {
  const baseName = (filename || 'event-card-image').replace(/\.[^.]+$/, '')
  return `${baseName}-card.jpg`
}

function getImageBoundsWithinCanvas(cropperCanvas, cropperImage) {
  if (!cropperCanvas || !cropperImage) {
    return null
  }

  const canvasRect = cropperCanvas.getBoundingClientRect()
  const imageRect = cropperImage.getBoundingClientRect()

  if (!canvasRect.width || !canvasRect.height || !imageRect.width || !imageRect.height) {
    return null
  }

  return {
    x: imageRect.left - canvasRect.left,
    y: imageRect.top - canvasRect.top,
    width: imageRect.width,
    height: imageRect.height
  }
}

function fitSelectionToBounds(selection, bounds, aspectRatio) {
  if (!selection || !bounds) {
    return
  }

  const maxWidth = bounds.width * 0.98
  const maxHeight = bounds.height * 0.98

  let width = Math.min(maxWidth, maxHeight * aspectRatio)
  let height = width / aspectRatio

  if (height > maxHeight) {
    height = maxHeight
    width = height * aspectRatio
  }

  const x = bounds.x + ((bounds.width - width) / 2)
  const y = bounds.y + ((bounds.height - height) / 2)

  selection.$change(x, y, width, height, aspectRatio)
  selection.$render()
}

function selectionWithinBounds(selectionDetail, bounds) {
  if (!selectionDetail || !bounds) {
    return true
  }

  return (
    selectionDetail.x >= bounds.x &&
    selectionDetail.y >= bounds.y &&
    (selectionDetail.x + selectionDetail.width) <= (bounds.x + bounds.width) &&
    (selectionDetail.y + selectionDetail.height) <= (bounds.y + bounds.height)
  )
}

function centerSelectionOnAxis(selection, bounds, axis) {
  if (!selection || !bounds) {
    return
  }

  const width = selection.width
  const height = selection.height
  let x = selection.x
  let y = selection.y

  if (!width || !height) {
    return
  }

  if (axis === 'horizontal') {
    x = bounds.x + ((bounds.width - width) / 2)
  }

  if (axis === 'vertical') {
    y = bounds.y + ((bounds.height - height) / 2)
  }

  selection.$change(x, y, width, height, selection.aspectRatio || CARD_IMAGE_ASPECT_RATIO)
  selection.$render()
}

function forceCropperCanvasSize(cropperCanvas, height) {
  if (!cropperCanvas || !height) {
    return
  }

  cropperCanvas.style.display = 'block'
  cropperCanvas.style.width = '100%'
  cropperCanvas.style.height = `${height}px`
  cropperCanvas.style.minHeight = `${height}px`
  cropperCanvas.style.maxHeight = `${height}px`
}

function initEventCardImageCropper() {
  const input = document.querySelector('[data-event-card-image-input]')
  const modalElement = document.querySelector('[data-event-card-image-modal]')

  if (!input || !modalElement || input.dataset.eventCardImageCropperInitialized === 'true') {
    return
  }

  if (!window.bootstrap || !window.bootstrap.Modal) {
    return
  }

  input.dataset.eventCardImageCropperInitialized = 'true'

  const slot = document.querySelector('[data-event-card-image-slot]')
  const previewContainer = slot?.querySelector('.ds-admin-event-form__media-preview') || null
  const previewImageSelector = '[data-event-card-image-preview]'
  const emptyStateSelector = '[data-event-card-image-empty]'
  const cropCurrentButton = slot?.querySelector('[data-event-card-image-crop-current]') || null
  const restoreOriginalButton = slot?.querySelector('[data-event-card-image-restore-original]') || null
  const status = slot?.querySelector('[data-event-card-image-status]') || null
  const container = modalElement.querySelector('[data-event-card-image-cropper-container]')
  const applyButton = modalElement.querySelector('[data-event-card-image-apply]')
  const centerHorizontalButton = modalElement.querySelector('[data-event-card-image-center-horizontal]')
  const centerVerticalButton = modalElement.querySelector('[data-event-card-image-center-vertical]')
  const modalDialog = modalElement.querySelector('.modal-dialog')
  const modalContent = modalElement.querySelector('.modal-content')
  const modalHeader = modalElement.querySelector('.modal-header')
  const modalBody = modalElement.querySelector('.modal-body')
  const modalFooter = modalElement.querySelector('.modal-footer')
  const modalHelp = modalElement.querySelector('.ds-admin-event-cropper__help')

  if (!slot || !container || !applyButton) {
    return
  }

  const getPreviewImage = () => slot.querySelector(previewImageSelector)
  const getEmptyState = () => slot.querySelector(emptyStateSelector)

  const modal = new window.bootstrap.Modal(modalElement)
  let cropper = null
  let sourceObjectUrl = null
  let previewObjectUrl = null
  let currentSourceUrl = slot.dataset.eventCardImageSource || ''
  const originalSourceUrl = currentSourceUrl
  const originalPreviewSrc = getPreviewImage()?.currentSrc || getPreviewImage()?.src || ''
  const restoreOriginalUrl = slot.dataset.eventCardImageRestoreUrl || ''
  let pendingFilename = 'event-card-image'
  let lastOpenMode = null
  let cropApplied = false
  let savedOriginalBackupAvailable = slot.dataset.eventCardImageHasOriginalBackup === 'true'

  const showStatus = (message, tone = 'success') => {
    if (!status) {
      return
    }

    status.hidden = false
    status.textContent = message
    status.dataset.tone = tone
  }

  const clearStatus = () => {
    if (!status) {
      return
    }

    status.hidden = true
    status.textContent = ''
    delete status.dataset.tone
  }

  const syncCropCurrentButton = () => {
    if (!cropCurrentButton) {
      return
    }

    cropCurrentButton.hidden = !(currentSourceUrl || getPreviewImage())
  }

  const syncRestoreOriginalButton = () => {
    if (!restoreOriginalButton) {
      return
    }

    const hasPendingFile = Boolean(input.files && input.files.length > 0)
    const canRestorePendingImage = Boolean(originalSourceUrl && hasPendingFile)
    const canRestoreSavedImage = Boolean(savedOriginalBackupAvailable && restoreOriginalUrl)

    restoreOriginalButton.hidden = !(canRestorePendingImage || canRestoreSavedImage)
  }

  const submitRestoreOriginalRequest = () => {
    if (!restoreOriginalUrl) {
      return
    }

    const form = document.createElement('form')
    const methodInput = document.createElement('input')
    const tokenInput = document.createElement('input')
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    form.method = 'post'
    form.action = restoreOriginalUrl
    form.hidden = true

    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'put'
    form.appendChild(methodInput)

    if (csrfToken) {
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const revokeObjectUrl = objectUrl => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
    }
  }

  const sizeModal = () => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1440
    const isMobile = viewportWidth <= 640
    const dialogWidth = isMobile ? Math.max(viewportWidth - 24, 320) : Math.min(viewportWidth * 0.92, 1100)
    const dialogHeight = isMobile ? Math.min(viewportHeight * 0.92, 820) : Math.min(viewportHeight * 0.88, 900)

    if (modalDialog) {
      modalDialog.style.width = `${dialogWidth}px`
      modalDialog.style.maxWidth = `${dialogWidth}px`
      modalDialog.style.height = `${dialogHeight}px`
      modalDialog.style.minHeight = `${dialogHeight}px`
      modalDialog.style.margin = '0 auto'
    }

    if (modalContent) {
      modalContent.style.width = '100%'
      modalContent.style.height = '100%'
      modalContent.style.minHeight = '100%'
      modalContent.style.borderRadius = isMobile ? '0.875rem' : '1rem'
    }

    if (!modalBody) {
      return
    }

    const bodyStyles = window.getComputedStyle(modalBody)
    const bodyVerticalPadding =
      (Number.parseFloat(bodyStyles.paddingTop) || 0) +
      (Number.parseFloat(bodyStyles.paddingBottom) || 0)

    const reservedHeight =
      (modalHeader?.offsetHeight || 0) +
      (modalFooter?.offsetHeight || 0) +
      (modalHelp?.offsetHeight || 0) +
      bodyVerticalPadding +
      24

    const containerHeight = Math.max(dialogHeight - reservedHeight, isMobile ? 320 : 480)
    container.style.height = `${containerHeight}px`
    container.style.minHeight = `${containerHeight}px`
  }

  const destroyCropper = () => {
    if (cropper) {
      cropper.destroy()
      cropper = null
    }

    container.innerHTML = ''
    revokeObjectUrl(sourceObjectUrl)
    sourceObjectUrl = null
    pendingFilename = 'event-card-image'
    applyButton.disabled = false
    applyButton.textContent = 'Apply crop'
  }

  const updatePreview = file => {
    let previewImage = getPreviewImage()
    const emptyState = getEmptyState()

    if (emptyState) {
      emptyState.remove()
    }

    if (!previewImage && previewContainer) {
      previewImage = document.createElement('img')
      previewImage.alt = 'Card image preview'
      previewImage.setAttribute('data-event-card-image-preview', '')
      previewContainer.prepend(previewImage)
    }

    if (!previewImage) {
      return
    }

    revokeObjectUrl(previewObjectUrl)
    previewObjectUrl = URL.createObjectURL(file)
    previewImage.src = previewObjectUrl
    syncCropCurrentButton()
    syncRestoreOriginalButton()
  }

  const restoreOriginalImage = () => {
    const hasPendingFile = Boolean(input.files && input.files.length > 0)

    if (hasPendingFile && originalSourceUrl) {
      const previewImage = getPreviewImage()

      input.value = ''
      currentSourceUrl = originalSourceUrl
      slot.dataset.eventCardImageSource = originalSourceUrl

      if (previewImage && originalPreviewSrc) {
        revokeObjectUrl(previewObjectUrl)
        previewObjectUrl = null
        previewImage.src = originalPreviewSrc
      }

      syncCropCurrentButton()
      syncRestoreOriginalButton()
      showStatus('Restored the original uploaded image.', 'success')
      return
    }

    if (savedOriginalBackupAvailable) {
      submitRestoreOriginalRequest()
    }
  }

  const openModal = (sourceUrl, filename, mode) => {
    destroyCropper()
    pendingFilename = filename
    lastOpenMode = mode
    cropApplied = false

    const image = new Image()
    image.alt = 'Crop card image'
    image.crossOrigin = 'anonymous'

    image.addEventListener('load', () => {
      const initializeCropper = () => {
        sizeModal()
        container.innerHTML = ''
        container.appendChild(image)

        cropper = new Cropper(image, { container })

        const selection = cropper.getCropperSelection()
        const cropperCanvas = cropper.getCropperCanvas()
        const cropperImage = cropper.getCropperImage()
        const containerHeight = container.getBoundingClientRect().height || container.clientHeight

        if (cropperCanvas && containerHeight) {
          forceCropperCanvasSize(cropperCanvas, containerHeight)
        }

        if (selection && cropperCanvas && cropperImage) {
          selection.aspectRatio = CARD_IMAGE_ASPECT_RATIO
          selection.initialAspectRatio = CARD_IMAGE_ASPECT_RATIO
          selection.initialCoverage = 1
          selection.movable = true
          selection.resizable = true
          selection.keyboard = true
          selection.outlined = true
          selection.precise = true

          const limitSelectionToImage = event => {
            const bounds = getImageBoundsWithinCanvas(cropperCanvas, cropperImage)

            if (!selectionWithinBounds(event.detail, bounds)) {
              event.preventDefault()
            }
          }

          selection.addEventListener('change', limitSelectionToImage)

          requestAnimationFrame(() => {
            forceCropperCanvasSize(cropperCanvas, containerHeight)

            requestAnimationFrame(() => {
              const bounds = getImageBoundsWithinCanvas(cropperCanvas, cropperImage)
              fitSelectionToBounds(selection, bounds, CARD_IMAGE_ASPECT_RATIO)
            })
          })
        }
      }

      if (modalElement.classList.contains('show')) {
        requestAnimationFrame(() => {
          initializeCropper()
        })
      } else {
        modalElement.addEventListener('shown.bs.modal', () => {
          requestAnimationFrame(() => {
            initializeCropper()
          })
        }, { once: true })

        sizeModal()
        modal.show()
      }
    }, { once: true })

    image.addEventListener('error', () => {
      destroyCropper()
      showStatus('We could not load that image for cropping.', 'error')
    }, { once: true })

    clearStatus()
    image.src = sourceUrl
  }

  const openModalForFile = file => {
    sourceObjectUrl = URL.createObjectURL(file)
    openModal(sourceObjectUrl, file.name, 'file')
  }

  const openCurrentImageForCropping = () => {
    const previewImage = getPreviewImage()
    const previewSourceUrl = previewImage?.currentSrc || previewImage?.src || ''
    const cropSourceUrl = currentSourceUrl || previewSourceUrl

    if (!cropSourceUrl) {
      showStatus('There is no uploaded card image to crop yet.', 'error')
      return
    }

    openModal(cropSourceUrl, 'event-card-image-current.jpg', 'current')
  }

  const centerSelection = axis => {
    if (!cropper) {
      return
    }

    const selection = cropper.getCropperSelection()
    const cropperCanvas = cropper.getCropperCanvas()
    const cropperImage = cropper.getCropperImage()
    const bounds = getImageBoundsWithinCanvas(cropperCanvas, cropperImage)

    centerSelectionOnAxis(selection, bounds, axis)
  }

  input.addEventListener('change', () => {
    const [file] = input.files || []

    if (!file) {
      return
    }

    if (!file.type.startsWith('image/')) {
      input.value = ''
      showStatus('Please choose an image file for the event card.', 'error')
      return
    }

    openModalForFile(file)
  })

  if (cropCurrentButton) {
    cropCurrentButton.addEventListener('click', event => {
      event.preventDefault()
      openCurrentImageForCropping()
    })
  }

  if (restoreOriginalButton) {
    restoreOriginalButton.addEventListener('click', event => {
      event.preventDefault()
      restoreOriginalImage()
    })
  }

  if (centerHorizontalButton) {
    centerHorizontalButton.addEventListener('click', event => {
      event.preventDefault()
      centerSelection('horizontal')
    })
  }

  if (centerVerticalButton) {
    centerVerticalButton.addEventListener('click', event => {
      event.preventDefault()
      centerSelection('vertical')
    })
  }

  applyButton.addEventListener('click', async event => {
    event.preventDefault()

    if (!cropper) {
      return
    }

    const selection = cropper.getCropperSelection()

    if (!selection) {
      showStatus('Crop selection was not ready. Please try again.', 'error')
      return
    }

    applyButton.disabled = true
    applyButton.textContent = 'Cropping...'

    try {
      const canvas = await selection.$toCanvas({
        width: OUTPUT_WIDTH,
        height: OUTPUT_HEIGHT
      })

      const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92)

      if (!blob) {
        throw new Error('Unable to create cropped image')
      }

      const croppedFile = new File(
        [blob],
        buildCroppedFilename(pendingFilename),
        { type: 'image/jpeg', lastModified: Date.now() }
      )

      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      input.files = dataTransfer.files

      updatePreview(croppedFile)
      currentSourceUrl = ''
      delete slot.dataset.eventCardImageSource
      cropApplied = true
      syncCropCurrentButton()
      syncRestoreOriginalButton()
      showStatus('Ready to upload. Card image cropped to the live 16:15 ratio.')
      modal.hide()
    } catch (error) {
      console.error('Event card image crop failed', error)
      showStatus('We could not crop that image. Please try another file.', 'error')
      applyButton.disabled = false
      applyButton.textContent = 'Apply crop'
    }
  })

  modalElement.addEventListener('hidden.bs.modal', () => {
    if (lastOpenMode === 'file' && !cropApplied) {
      input.value = ''
    }

    lastOpenMode = null
    cropApplied = false
    destroyCropper()
  })

  window.addEventListener('resize', () => {
    if (!modalElement.classList.contains('show')) {
      return
    }

    sizeModal()
  })

  syncCropCurrentButton()
  syncRestoreOriginalButton()
}

READY_EVENTS.forEach(eventName => {
  document.addEventListener(eventName, initEventCardImageCropper)
})
